<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-3">
      <select v-model="filters.kind" :class="[SIZED_INPUT, 'w-48']" aria-label="Kind">
        <option value="">All kinds</option>
        <option v-for="(n, k) in summary.pending_by_kind" :key="k" :value="k">{{ k }} ({{ n }})</option>
      </select>
      <select v-model="filters.executor" :class="[SIZED_INPUT, 'w-48']" aria-label="Executor">
        <option value="">All executors</option>
        <option v-for="e in executors" :key="e.key" :value="e.key">{{ e.name }}</option>
      </select>
      <SearchableSelect v-model="filters.template" :options="templateChoices" placeholder="All templates" search-placeholder="Search templates" aria-label="Template" clearable class="w-64" />
      <span class="ml-auto text-gray-500">{{ summary.pending || 0 }} pending across {{ summary.pending_runs || 0 }} runs</span>
      <button type="button" :class="TEXT_BUTTON" @click="refresh">Refresh</button>
    </div>

    <div v-if="notice" role="alert" :class="['rounded-md px-4 py-3 text-gray-900', notice.bad ? 'bg-red-tint' : 'bg-neutral-50']">
      {{ notice.text }}
      <ul v-if="notice.errors && notice.errors.length" class="mt-2 list-disc pl-6">
        <li v-for="(e, i) in notice.errors" :key="i">Effect #{{ e.id }}: {{ e.error }}</li>
      </ul>
    </div>

    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-7 self-start bg-white rounded-lg border border-gray-200">
        <div v-if="selected.length" class="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-neutral-50">
          <span class="text-gray-900">{{ selected.length }} selected · {{ selectedBreakdown }}</span>
          <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" @click="selected = []">Clear</button>
          <button type="button" :class="TEXT_BUTTON" @click="bulk = { approved: false }">Decline selected</button>
          <button type="button" :class="SECONDARY_BUTTON" @click="bulk = { approved: true }">Approve selected…</button>
        </div>
        <table class="w-full table-fixed">
          <colgroup><col style="width:6%"><col style="width:13%"><col style="width:43%"><col style="width:17%"><col style="width:21%"></colgroup>
          <thead class="bg-gray-50 border-b border-gray-200"><tr>
            <th :class="TH" /><th :class="TH">Kind</th><th :class="TH">Write</th><th :class="TH">Patient</th><th :class="[TH, 'text-right']">Actions</th>
          </tr></thead>
          <tbody v-if="loading">
            <tr v-for="n in 3" :key="n"><td v-for="c in 5" :key="c" :class="TD"><div class="h-4 rounded bg-gray-100" /></td></tr>
          </tbody>
          <tbody v-else-if="!groups.length">
            <tr><td colspan="5" class="px-3 py-8 text-center text-gray-500">
              {{ anyFilter ? 'No pending effects match these filters.' : 'Nothing waiting for approval.' }}
              <button v-if="anyFilter" type="button" :class="[TEXT_BUTTON, 'ml-2']" @click="clearFilters">Clear filters</button>
            </td></tr>
          </tbody>
          <tbody v-for="g in groups" v-else :key="g.root" class="border-b border-gray-200">
            <tr class="bg-gray-50">
              <td class="px-3 py-2"><input type="checkbox" :checked="groupChecked(g)" :aria-label="`Select run ${g.root}`" @change="toggleGroup(g)"></td>
              <td colspan="4" class="px-3 py-2 text-gray-700">
                <router-link :to="{ name: 'AdminHermesRun', params: { runId: g.root } }" class="font-medium text-neutral-800">Run #{{ g.root }}</router-link>
                · {{ g.template }} · {{ g.executor }} · proposed <span :title="absolute(g.newest)">{{ relative(g.newest) }}</span> · {{ g.items.length }} pending
              </td>
            </tr>
            <tr
v-for="e in g.items" :key="e.id"
                :class="['cursor-pointer', current && current.id === e.id ? 'bg-primary-50' : 'hover:bg-gray-50', decided[e.id] ? 'text-gray-500' : '']"
                @click="select(e)">
              <td :class="['px-3 py-3 align-top', current && current.id === e.id ? 'border-l-2 border-primary-500' : (selected.includes(e.id) ? 'border-l-2 border-neutral-700' : '')]" @click.stop>
                <input v-if="!decided[e.id]" v-model="selected" type="checkbox" :value="e.id" :aria-label="`Select effect ${e.id}`">
              </td>
              <td class="px-3 py-3 align-top">{{ e.kind }}</td>
              <td class="px-3 py-3 align-top">
                <div>{{ e.label }}: {{ effectSummary(e) }}</div>
                <div class="text-gray-500">Effect #{{ e.id }} · run #{{ e.run }}</div>
              </td>
              <td class="px-3 py-3 align-top">
                <div class="sensitive truncate" :title="e.unit_key">{{ e.unit_key || '—' }}</div>
                <div v-if="e.entity_name" class="sensitive text-gray-600">{{ e.entity_name }}</div>
              </td>
              <td class="px-3 py-3 align-top text-right whitespace-nowrap" @click.stop>
                <span v-if="decided[e.id]">{{ decided[e.id] }}</span>
                <template v-else>
                  <button type="button" :class="TEXT_BUTTON" :disabled="busy" @click="decide(e, true)">Approve</button>
                  <button type="button" :class="[TEXT_BUTTON, 'ml-3']" :disabled="busy" @click="decide(e, false)">Decline</button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <aside class="col-span-5">
        <div class="sticky top-4 bg-white rounded-lg border border-gray-200">
          <div v-if="!current" class="p-4 text-gray-500">Select a write to see its payload, the result behind it and its run.</div>
          <template v-else>
            <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
              <span class="font-semibold text-gray-900">Effect #{{ current.id }}</span>
              {{ current.kind }}
              <HermesBadge :value="current.state" />
              <button type="button" class="ml-auto text-gray-500 hover:text-gray-900" @click="closeCurrent">Close</button>
            </div>
            <div class="p-4 space-y-4">
              <div>
                <div class="text-gray-900">{{ current.label }}: {{ effectSummary(current) }}</div>
                <div class="text-gray-500 mt-2">
                  Proposed by {{ current.executor_key }} <span :title="absolute(current.created_at)">{{ relative(current.created_at) }}</span>.
                  Approving writes it now; it cannot be undone from here.
                </div>
              </div>
              <div v-if="current.state === 'pending' && !decided[current.id]" class="flex gap-3">
                <button type="button" :class="PRIMARY_BUTTON" :disabled="busy" @click="decide(current, true)">Approve</button>
                <button type="button" :class="SECONDARY_BUTTON" :disabled="busy" @click="decide(current, false)">Decline</button>
              </div>
              <div v-else-if="current.state === 'declined'" class="rounded-md bg-neutral-50 px-3 py-2 text-gray-900">
                Declined by <span class="sensitive">{{ current.decided_by_name || 'someone' }}</span>. Not executed; nothing was written.
              </div>
              <div v-else-if="current.state === 'finished' && current.ok === false" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">
                <strong>Execution failed.</strong> {{ (current.result || {}).error }}
              </div>
              <section>
                <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Payload</h3>
                <pre class="rounded-md bg-gray-50 border border-gray-200 p-3 whitespace-pre-wrap break-words sensitive" data-demo-blur>{{ JSON.stringify(current.payload, null, 2) }}</pre>
              </section>
              <section v-if="context">
                <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Result that produced it</h3>
                <p v-if="!context.results.length" class="text-gray-500">This run recorded no result for the write.</p>
                <div v-for="r in context.results" :key="r.id" class="space-y-2">
                  <dl class="grid grid-cols-2 gap-x-3 gap-y-2">
                    <template v-for="(v, k) in scalarFields(r.data)" :key="k">
                      <dt class="text-gray-500">{{ words(k) }}</dt><dd class="text-gray-900">{{ v }}</dd>
                    </template>
                  </dl>
                  <div v-for="(text, k) in proseFields(r.data)" :key="k" class="rounded-md bg-gray-50 px-3 py-2 text-gray-800">
                    <div class="text-gray-500 mb-1">{{ words(k) }}</div>
                    <p class="sensitive whitespace-pre-wrap" data-demo-blur>{{ text }}</p>
                  </div>
                </div>
                <p v-if="context.result_count > context.results.length" class="text-gray-500">{{ context.result_count - context.results.length }} more results in this run.</p>
              </section>
              <section v-if="context">
                <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Run</h3>
                <dl class="grid grid-cols-2 gap-x-3 gap-y-2">
                  <dt class="text-gray-500">Run</dt><dd><router-link :to="{ name: 'AdminHermesRun', params: { runId: context.root.id } }" class="text-neutral-800">#{{ context.root.id }}</router-link> <HermesBadge :value="context.root.status" /></dd>
                  <dt class="text-gray-500">Template</dt><dd>{{ context.root.template_name }} v{{ context.root.template_version }}</dd>
                  <dt class="text-gray-500">Executor</dt><dd>{{ context.root.executor_key }} · {{ context.root.executor_model }}</dd>
                  <dt class="text-gray-500">Trigger</dt><dd>{{ words(context.root.trigger) }}<span v-if="context.root.requested_by_name"> · <span class="sensitive">{{ context.root.requested_by_name }}</span></span></dd>
                  <dt class="text-gray-500">Progress</dt><dd>{{ progressLine(context.root) }}</dd>
                  <dt class="text-gray-500">Cost</dt><dd class="tabular-nums">{{ money((context.root.usage || {}).cost_usd) }}</dd>
                </dl>
              </section>
            </div>
          </template>
        </div>
      </aside>
    </div>

    <HermesCard title="Decided" :meta="historyMeta" collapsible flush>
      <table class="w-full table-fixed divide-y divide-gray-200">
        <colgroup><col style="width:10%"><col style="width:11%"><col style="width:12%"><col style="width:9%"><col style="width:26%"><col style="width:10%"><col style="width:6%"><col style="width:16%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH">Decided</th><th :class="TH">Decision</th><th :class="TH">By</th><th :class="TH">Kind</th>
          <th :class="TH">Write</th><th :class="TH">Patient</th><th :class="TH">Run</th><th :class="TH">Outcome</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-if="!history.length"><td colspan="8" class="px-3 py-8 text-center text-gray-500">No decisions yet.</td></tr>
          <tr v-for="e in history" :key="e.id" class="cursor-pointer hover:bg-gray-50" @click="select(e)">
            <td :class="TD" :title="absolute(e.decided_at)">{{ relative(e.decided_at || e.created_at) }}</td>
            <td :class="TD"><HermesBadge :value="e.state === 'declined' ? 'declined' : 'completed'" :label="e.state === 'declined' ? 'Declined' : (e.mode === 'direct' ? 'Direct' : 'Approved')" /></td>
            <td :class="TD"><span class="sensitive">{{ e.decided_by_name || (e.mode === 'direct' ? 'Executor' : '') }}</span></td>
            <td :class="TD">{{ e.kind }}</td>
            <td :class="TD">{{ effectSummary(e) }}</td>
            <td :class="TD"><span class="sensitive truncate block" :title="e.unit_key">{{ e.unit_key || '—' }}</span></td>
            <td :class="TD"><router-link :to="{ name: 'AdminHermesRun', params: { runId: e.root_run } }" class="text-neutral-800" @click.stop>#{{ e.root_run }}</router-link></td>
            <td :class="TD">
              <span v-if="e.state === 'declined'" class="text-gray-600">Not executed; nothing was written.</span>
              <HermesBadge v-else-if="e.state === 'executing'" value="executing" />
              <div v-else-if="e.ok === false" class="rounded bg-red-tint px-2 py-1 text-gray-900"><strong>Execution failed.</strong> {{ (e.result || {}).error }}</div>
              <HermesBadge v-else value="finished" label="Finished ok" />
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="historyNext" class="px-4 py-3 border-t border-gray-200"><button type="button" :class="TEXT_BUTTON" @click="loadHistory(true)">Load older</button></div>
    </HermesCard>

    <div v-if="bulk" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true">
      <div class="w-[560px] rounded-lg bg-white border border-gray-200">
        <div class="bg-primary-100 px-4 py-3 rounded-t-lg font-semibold text-gray-900">
          {{ bulk.approved ? `Approve ${selected.length} writes?` : `Decline ${selected.length} proposals?` }}
        </div>
        <div class="p-4 space-y-3 text-gray-700">
          <p v-if="bulk.approved">{{ selected.length }} writes on {{ selectedPatients }} patients from {{ selectedRuns }} runs will execute immediately.</p>
          <p v-else>Nothing will be written for these {{ selected.length }} proposals.</p>
          <ul class="max-h-64 overflow-y-auto space-y-1">
            <li v-for="e in selectedEffects" :key="e.id">{{ e.label }}: {{ effectSummary(e) }} · <span class="sensitive">{{ e.unit_key }}</span></li>
          </ul>
        </div>
        <div class="flex justify-end gap-3 px-4 py-3 border-t border-gray-200">
          <button type="button" :class="SECONDARY_BUTTON" :disabled="busy" @click="bulk = null">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" :disabled="busy" @click="runBulk">
            {{ busy ? 'Working…' : (bulk.approved ? `Approve ${selected.length} writes` : `Decline ${selected.length}`) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import queryState from '@/mixins/queryState';
import HermesBadge from './HermesBadge.vue';
import HermesCard from './HermesCard.vue';
import SearchableSelect from '@/components/widgetcomponents/library/SearchableSelect.vue';
import { API, INPUT, SIZED_INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH, absolute, effectSummary, errorOf, isProse, money, progressLine, relative, templateOptions, words } from './hermes';

export default {
  name: 'HermesApprovals',
  components: { HermesBadge, HermesCard, SearchableSelect },
  mixins: [queryState('filters', { kind: '', executor: '', template: '' })],
  emits: ['pending'],
  data() {
    return {
      loading: true, busy: false, notice: null, pending: [], history: [], historyNext: null, summary: {},
      executors: [], templates: [],
      selected: [], current: null, context: null, decided: {}, bulk: null,
      INPUT, SIZED_INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    templateChoices() {
      return templateOptions(this.templates);
    },
    focusId() {
      return String(this.$route.query.effect || '');
    },
    anyFilter() {
      return Object.values(this.filters).some(Boolean);
    },
    groups() {
      const out = [];
      const byRoot = {};
      this.pending.forEach((e) => {
        if (!byRoot[e.root_run]) {
          byRoot[e.root_run] = { root: e.root_run, template: e.template_name, executor: e.executor_key, newest: e.created_at, items: [] };
          out.push(byRoot[e.root_run]);
        }
        const g = byRoot[e.root_run];
        g.items.push(e);
        if (e.created_at > g.newest) g.newest = e.created_at;
      });
      return out;
    },
    selectedEffects() {
      return this.pending.filter((e) => this.selected.includes(e.id));
    },
    selectedBreakdown() {
      const counts = {};
      this.selectedEffects.forEach((e) => { counts[e.kind] = (counts[e.kind] || 0) + 1; });
      return Object.entries(counts).map(([k, n]) => `${n} ${k}`).join(', ');
    },
    selectedPatients() {
      return new Set(this.selectedEffects.map((e) => e.unit_key).filter(Boolean)).size;
    },
    selectedRuns() {
      return new Set(this.selectedEffects.map((e) => e.root_run)).size;
    },
    historyMeta() {
      const d = this.summary.decided_7d || {};
      return `Last 7 days: ${d.approved || 0} approved, ${d.declined || 0} declined, ${d.failed || 0} failed`;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.load();
      },
    },
    focusId(id) {
      if (!id) this.current = null;
      else this.restoreFocus();
    },
  },
  mounted() {
    this.loadLookups();
    this.refresh();
  },
  methods: {
    absolute, effectSummary, money, progressLine, relative, words,
    async loadLookups() {
      const [e, t] = await Promise.all([this.$axios.get(`${API}/executors/`), this.$axios.get(`${API}/templates/`)]);
      this.executors = e.data;
      this.templates = t.data;
    },
    refresh() {
      this.decided = {};
      this.load();
      this.loadHistory();
    },
    async load() {
      this.loading = true;
      try {
        const params = { state: 'pending', limit: 500, ...Object.fromEntries(Object.entries(this.filters).filter(([, v]) => v)) };
        const [list, summary] = await Promise.all([
          this.$axios.get(`${API}/effects/`, { params }), this.$axios.get(`${API}/effects/summary/`),
        ]);
        this.pending = list.data.results;
        this.summary = summary.data;
        this.$emit('pending', summary.data.pending);
        this.selected = this.selected.filter((id) => this.pending.some((e) => e.id === id));
        this.restoreFocus();
      } catch (e) {
        this.notice = { bad: true, text: errorOf(e, 'The queue could not be loaded.') };
      } finally {
        this.loading = false;
      }
    },
    async loadHistory(more = false) {
      const url = more && this.historyNext ? this.historyNext : `${API}/effects/`;
      const { data } = await this.$axios.get(url, more ? {} : { params: { state: 'finished,declined,executing', limit: 50 } });
      this.history = more ? this.history.concat(data.results) : data.results;
      this.historyNext = data.next;
      this.restoreFocus();
    },
    restoreFocus() {
      if (!this.focusId || (this.current && String(this.current.id) === this.focusId)) return;
      const match = [...this.pending, ...this.history].find((e) => String(e.id) === this.focusId);
      if (match) this.select(match);
    },
    closeCurrent() {
      this.current = null;
      this.setFocus('', 'push');
    },
    setFocus(id, how) {
      if (id === this.focusId) return;
      const query = { ...this.$route.query };
      if (id) query.effect = id;
      else delete query.effect;
      this.$router[how]({ query });
    },
    clearFilters() {
      this.filters = { kind: '', executor: '', template: '' };
    },
    groupChecked(g) {
      const open = g.items.filter((e) => !this.decided[e.id]).map((e) => e.id);
      return open.length > 0 && open.every((id) => this.selected.includes(id));
    },
    toggleGroup(g) {
      const open = g.items.filter((e) => !this.decided[e.id]).map((e) => e.id);
      this.selected = this.groupChecked(g) ? this.selected.filter((id) => !open.includes(id)) : [...new Set([...this.selected, ...open])];
    },
    async select(e, how = 'push') {
      this.current = e;
      this.setFocus(String(e.id), how);
      this.context = null;
      try {
        const { data } = await this.$axios.get(`${API}/effects/${e.id}/context/`);
        if (this.current && this.current.id === e.id) this.context = data;
      } catch {
        this.context = null;
      }
    },
    scalarFields(data) {
      return Object.fromEntries(Object.entries(data || {}).filter(([k, v]) => v !== null && typeof v !== 'object' && !isProse(k, null, v)).map(([k, v]) => [k, words(v)]));
    },
    proseFields(data) {
      return Object.fromEntries(Object.entries(data || {}).filter(([k, v]) => isProse(k, null, v)).map(([k, v]) => [k, typeof v === 'object' ? v.text : v]));
    },
    async decide(e, approved) {
      this.busy = true;
      this.notice = null;
      try {
        const { data } = await this.$axios.post(`${API}/effects/${e.id}/decide/`, { approved });
        this.decided = { ...this.decided, [e.id]: approved ? (data.ok === false ? 'Approved · execution failed' : 'Approved') : 'Declined' };
        this.selected = this.selected.filter((id) => id !== e.id);
        if (data.ok === false) this.notice = { bad: true, text: `Effect #${e.id} was approved but failed to execute: ${(data.result || {}).error}` };
        if (this.current && this.current.id === e.id) {
          this.current = { ...this.current, ...data };
          const next = this.pending.find((x) => !this.decided[x.id] && x.id !== e.id);
          if (next) this.select(next, 'replace');
        }
        this.$emit('pending', Math.max(0, (this.summary.pending || 1) - Object.keys(this.decided).length));
      } catch (err) {
        this.notice = { bad: true, text: errorOf(err, 'The decision was not recorded.') };
        this.load();
      } finally {
        this.busy = false;
      }
    },
    async runBulk() {
      this.busy = true;
      try {
        const { data } = await this.$axios.post(`${API}/effects/bulk-decide/`, { ids: this.selected, approved: this.bulk.approved });
        const errors = data.outcomes.filter((o) => o.error);
        const marks = {};
        data.outcomes.forEach((o) => { marks[o.id] = this.bulk.approved ? (o.error ? 'Approved · execution failed' : 'Approved') : 'Declined'; });
        this.decided = { ...this.decided, ...marks };
        const ok = data.outcomes.length - errors.length;
        this.notice = errors.length
          ? { bad: true, text: `${ok} of ${data.outcomes.length} finished ok; ${errors.length} failed.`, errors }
          : { bad: false, text: `${data.outcomes.length} ${this.bulk.approved ? 'approved' : 'declined'}.` };
        this.selected = [];
        this.bulk = null;
        this.loadHistory();
      } catch (err) {
        this.notice = { bad: true, text: errorOf(err, 'The decisions were not recorded.') };
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
